import React, { useRef, useState } from 'react';
import {
    Button,
    Modal,
    Upload,
    Table,
    Alert,
    Space,
    Typography,
    Tag,
    Tooltip,
    Select,
    Row,
    Col,
    Divider,
    Badge,
    Progress,
    List,
    Input,
    Popconfirm,
    Empty,
    Spin,
    theme
} from 'antd';
import {
    UploadOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    GlobalOutlined,
    TranslationOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    SaveOutlined,
    DeleteOutlined,
    FolderOpenOutlined,
    EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { ScheduleType, PushTemplate, SchedulePreset } from '../../types/push_management.types';
import { Segment } from '../../types/push.types';
import { translateBatch, SUPPORTED_LANGUAGES } from '../../utils/translation';
import { useAppDispatch, useAppSelector } from 'store';
import {
    createTemplate,
    fetchPresets,
    createPreset,
    updatePreset,
    deletePreset,
    selectPresets,
    selectPushLoading,
} from 'store/slices/push.slice';

const { Text, Title } = Typography;
const { TextArea } = Input;
const LANG_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
    value: l.code,
    label: `${l.abbr} — ${l.name}`,
}));

const TIMEZONES = [
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'Europe/London (GMT)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
    { value: 'America/New_York', label: 'America/New_York (EST)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' },
];

const WEEKDAYS = [
    { value: 'monday', label: 'Понедельник' },
    { value: 'tuesday', label: 'Вторник' },
    { value: 'wednesday', label: 'Среда' },
    { value: 'thursday', label: 'Четверг' },
    { value: 'friday', label: 'Пятница' },
    { value: 'saturday', label: 'Суббота' },
    { value: 'sunday', label: 'Воскресенье' },
];

const SCHEDULE_TYPE_CONFIG: Record<ScheduleType, { color: string; label: string }> = {
    daily: { color: 'blue', label: 'Ежедневно' },
    weekly: { color: 'green', label: 'Еженедельно' },
    monthly: { color: 'orange', label: 'Ежемесячно' },
};

export interface InlinePushContent {
    title: string;
    body: string;
    image_url?: string;
}

export interface TemplateScheduleItem {
    name: string;
    description?: string;
    schedule_type: ScheduleType;
    schedule_value?: string;
    schedule_time: string;
    timezone?: string;
    is_active?: boolean;
    segment_id?: number;
    push_template_id?: number;
    push?: InlinePushContent;
}

export interface ScheduleTemplate {
    name?: string;
    lang?: string;
    default_timezone?: string;
    default_segment_id?: number;
    schedules: TemplateScheduleItem[];
}

export interface LoadedSchedule {
    name: string;
    description?: string;
    schedule_type: ScheduleType;
    schedule_value: string;
    schedule_time: string;
    timezone: string;
    is_active: boolean;
    push_template_id: number;
    segment_id?: number;
}

interface RecurringScheduleLoaderProps {
    disabled?: boolean;
    appId?: number;
    segments?: Segment[];
    templates?: PushTemplate[];
    onLoad: (schedules: LoadedSchedule[], templateName?: string, targetLang?: string) => void;
}

function parseScheduleTemplate(raw: unknown): ScheduleTemplate {
    if (typeof raw !== 'object' || raw === null || !('schedules' in raw)) {
        throw new Error('Неверный формат: ожидается объект с полем "schedules"');
    }
    const tpl = raw as Record<string, unknown>;
    if (!Array.isArray(tpl.schedules) || tpl.schedules.length === 0) {
        throw new Error('Поле "schedules" должно быть непустым массивом');
    }
    tpl.schedules.forEach((s: unknown, i: number) => {
        if (typeof s !== 'object' || s === null) {
            throw new Error(`schedules[${i}]: ожидается объект`);
        }
        const item = s as Record<string, unknown>;
        if (!item.name) throw new Error(`schedules[${i}]: отсутствует поле "name"`);
        if (!item.schedule_type) throw new Error(`schedules[${i}]: отсутствует поле "schedule_type"`);
        if (!['daily', 'weekly', 'monthly'].includes(item.schedule_type as string)) {
            throw new Error(`schedules[${i}]: недопустимый schedule_type "${item.schedule_type}"`);
        }
        if (!item.schedule_time) throw new Error(`schedules[${i}]: отсутствует поле "schedule_time"`);
        if (item.schedule_type === 'weekly' && !item.schedule_value) {
            throw new Error(`schedules[${i}]: для weekly требуется schedule_value`);
        }
        if (item.schedule_type === 'monthly' && !item.schedule_value) {
            throw new Error(`schedules[${i}]: для monthly требуется schedule_value`);
        }
        if (!item.push_template_id && !item.push) {
            if (item.name && item.description) {
                item.push = {
                    title: item.name as string,
                    body: item.description as string,
                };
            } else {
                throw new Error(
                    `schedules[${i}]: нужен "push_template_id", "push" с контентом, или "name" + "description" для автосоздания шаблона`
                );
            }
        }
        if (item.push) {
            const push = item.push as Record<string, unknown>;
            if (!push.title || !push.body) {
                throw new Error(`schedules[${i}].push: обязательны поля "title" и "body"`);
            }
        }
    });
    return raw as ScheduleTemplate;
}

function normalizePresetData(data: unknown): unknown {
    if (typeof data === 'string') {
        return JSON.parse(data);
    }
    return data;
}

function getPresetScheduleCount(data: unknown): number | null {
    try {
        const obj = normalizePresetData(data) as { schedules?: unknown[] };
        return Array.isArray(obj?.schedules) ? obj.schedules.length : null;
    } catch {
        return null;
    }
}

function buildScheduleValue(item: TemplateScheduleItem): string {
    switch (item.schedule_type) {
        case 'daily': return item.schedule_time;
        case 'weekly': return item.schedule_value ?? '';
        case 'monthly': return item.schedule_value ?? '';
        default: return item.schedule_time;
    }
}

function formatScheduleDisplay(item: TemplateScheduleItem): string {
    const weekdayLabel = WEEKDAYS.find(w => w.value === item.schedule_value)?.label;
    switch (item.schedule_type) {
        case 'daily': return `Каждый день в ${item.schedule_time}`;
        case 'weekly': return `Каждый ${weekdayLabel ?? item.schedule_value} в ${item.schedule_time}`;
        case 'monthly': return `${item.schedule_value}-го числа в ${item.schedule_time}`;
        default: return item.schedule_time;
    }
}

function norm(s: string): string {
    return s.toLowerCase().trim().replace(/\s+/g, ' ');
}

function findTemplateByContent(
    templates: PushTemplate[],
    title: string,
    body: string,
): PushTemplate | undefined {
    const nTitle = norm(title);
    const nBody = norm(body);
    return templates.find(t => norm(t.title) === nTitle && norm(t.body) === nBody);
}

type Step = 'upload' | 'edit' | 'processing' | 'preview';

const DEFAULT_TIMEZONE = 'UTC';

const RecurringScheduleLoader: React.FC<RecurringScheduleLoaderProps> = ({
    disabled = false,
    appId,
    segments = [],
    templates: existingTemplates = [],
    onLoad,
}) => {
    const { token } = theme.useToken();
    const dispatch = useAppDispatch();
    const presets = useAppSelector(selectPresets);
    const presetsLoading = useAppSelector(selectPushLoading);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<Step>('upload');
    const [template, setTemplate] = useState<ScheduleTemplate | null>(null);
    const [globalTimezone, setGlobalTimezone] = useState<string>(DEFAULT_TIMEZONE);
    const [globalSegmentId, setGlobalSegmentId] = useState<number | undefined>(undefined);
    const [overrides, setOverrides] = useState<Record<number, Partial<TemplateScheduleItem>>>({});
    const [targetLang, setTargetLang] = useState<string | undefined>(undefined);
    type TranslatedOverride = { name: string; description?: string; push?: InlinePushContent };
    const [translatedOverrides, setTranslatedOverrides] = useState<Record<number, TranslatedOverride>>({});
    const [resolvedTemplateIds, setResolvedTemplateIds] = useState<Record<number, number>>({});
    const [progress, setProgress] = useState(0);
    const [progressLabel, setProgressLabel] = useState('');
    const [processingLog, setProcessingLog] = useState<string[]>([]);
    const stopRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [loadedFromPresetId, setLoadedFromPresetId] = useState<number | undefined>(undefined);
    const [savePresetOpen, setSavePresetOpen] = useState(false);
    const [savePresetMode, setSavePresetMode] = useState<'create' | 'update'>('create');
    const [presetName, setPresetName] = useState('');
    const [presetDescription, setPresetDescription] = useState('');
    const [savingPreset, setSavingPreset] = useState(false);
    const inlinePushCount = template?.schedules.filter(s => s.push && !s.push_template_id).length ?? 0;
    const reset = () => {
        setStep('upload');
        setTemplate(null);
        setGlobalTimezone(DEFAULT_TIMEZONE);
        setGlobalSegmentId(undefined);
        setOverrides({});
        setTargetLang(undefined);
        setTranslatedOverrides({});
        setResolvedTemplateIds({});
        setProgress(0);
        setProgressLabel('');
        setProcessingLog([]);
        setError(null);
        setLoadedFromPresetId(undefined);
        stopRef.current = false;
    };

    const handleOpen = () => {
        reset();
        setOpen(true);
        dispatch(fetchPresets());
    };

    const handleClose = () => {
        stopRef.current = true;
        setOpen(false);
    };

    const addProcessingLog = (msg: string) => {
        setProcessingLog(prev => [...prev, msg]);
    };

    const getMergedItem = (item: TemplateScheduleItem, index: number): TemplateScheduleItem => ({
        ...item,
        ...overrides[index],
        ...(translatedOverrides[index]
            ? {
                name: translatedOverrides[index].name,
                description: translatedOverrides[index].description,
                push: translatedOverrides[index].push ?? item.push,
            }
            : {}
        ),
        timezone: overrides[index]?.timezone ?? item.timezone ?? globalTimezone,
        is_active: overrides[index]?.is_active !== undefined
            ? overrides[index].is_active
            : item.is_active !== undefined ? item.is_active : true,
        segment_id: overrides[index]?.segment_id !== undefined
            ? overrides[index].segment_id
            : item.segment_id !== undefined ? item.segment_id : globalSegmentId,
    });

    const handleFileRead = (file: UploadFile): boolean => {
        setError(null);
        const raw = file.originFileObj ?? (file as unknown as File);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsed = JSON.parse(e.target?.result as string);
                const tpl = parseScheduleTemplate(parsed);
                setTemplate(tpl);
                if (tpl.default_timezone) setGlobalTimezone(tpl.default_timezone);
                if (tpl.default_segment_id) setGlobalSegmentId(tpl.default_segment_id);
                setLoadedFromPresetId(undefined);
                setStep('edit');
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Ошибка парсинга JSON');
            }
        };
        reader.readAsText(raw);
        return false;
    };

    const handleLoadPreset = (preset: SchedulePreset) => {
        setError(null);
        try {
            const raw = normalizePresetData(preset.json_data);
            const tpl = parseScheduleTemplate(raw);
            setTemplate(tpl);
            if (tpl.default_timezone) setGlobalTimezone(tpl.default_timezone);
            if (tpl.default_segment_id) setGlobalSegmentId(tpl.default_segment_id);
            setLoadedFromPresetId(preset.id);
            setStep('edit');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Ошибка пресета');
        }
    };

    const handleDeletePreset = async (id: number) => {
        try {
            await dispatch(deletePreset(id)).unwrap();
            if (loadedFromPresetId === id) setLoadedFromPresetId(undefined);
        } catch (err: any) {
            setError(err?.message ?? 'Не удалось удалить пресет');
        }
    };

    const openCreatePresetDialog = () => {
        setSavePresetMode('create');
        setPresetName(template?.name ?? '');
        setPresetDescription('');
        setSavePresetOpen(true);
    };

    const openUpdatePresetDialog = () => {
        const current = presets.find(p => p.id === loadedFromPresetId);
        setSavePresetMode('update');
        setPresetName(current?.name ?? template?.name ?? '');
        setPresetDescription(current?.description ?? '');
        setSavePresetOpen(true);
    };

    const handleSavePreset = async () => {
        if (!template) return;
        const name = presetName.trim();
        if (!name) {
            setError('Название пресета обязательно');
            return;
        }
        setSavingPreset(true);
        try {
            if (savePresetMode === 'update' && loadedFromPresetId) {
                await dispatch(updatePreset({
                    id: loadedFromPresetId,
                    data: {
                        name,
                        description: presetDescription.trim() || undefined,
                        json_data: template,
                    },
                })).unwrap();
                dispatch(fetchPresets());
            } else {
                const result = await dispatch(createPreset({
                    name,
                    description: presetDescription.trim() || undefined,
                    json_data: template,
                })).unwrap();
                if (result.preset?.id) {
                    setLoadedFromPresetId(result.preset.id);
                }
            }
            setSavePresetOpen(false);
            setPresetName('');
            setPresetDescription('');
            setError(null);
        } catch (err: any) {
            setError(err?.message ?? 'Не удалось сохранить пресет');
        } finally {
            setSavingPreset(false);
        }
    };

    const handleProcess = async () => {
        if (!template) return;

        const srcLang = template.lang ?? 'en';
        const needTranslate = targetLang && targetLang !== srcLang;
        const hasInlinePush = template.schedules.some(s => s.push && !s.push_template_id);
        const needProcessing = needTranslate || hasInlinePush;

        if (!needProcessing) {
            setTranslatedOverrides({});
            setResolvedTemplateIds({});
            setStep('preview');
            return;
        }

        stopRef.current = false;
        setStep('processing');
        setProgress(0);
        setProcessingLog([]);

        const templateCache = [...existingTemplates];
        const newResolvedIds: Record<number, number> = {};
        const newTranslated: Record<number, { name: string; description?: string; push?: InlinePushContent }> = {};

        if (needTranslate) {
            addProcessingLog('🌍 Перевод...');

            const items: { key: string; text: string }[] = [];
            template.schedules.forEach((s, i) => {
                items.push({ key: `${i}_name`, text: s.name });
                if (s.description) {
                    items.push({ key: `${i}_desc`, text: s.description });
                }
                if (s.push) {
                    items.push({ key: `${i}_push_title`, text: s.push.title });
                    items.push({ key: `${i}_push_body`, text: s.push.body });
                }
            });

            try {
                const translations = await translateBatch(
                    items,
                    {
                        from: srcLang,
                        to: targetLang!,
                        delay: 150,
                        onProgress: (done, total, key) => {
                            const pct = Math.round((done / total) * (hasInlinePush ? 50 : 100));
                            setProgress(pct);
                            if (key.includes('push_title')) setProgressLabel('Push заголовок...');
                            else if (key.includes('push_body')) setProgressLabel('Push текст...');
                            else if (key.endsWith('_name')) setProgressLabel('Название...');
                            else setProgressLabel('Описание...');
                        },
                    },
                    stopRef,
                );

                if (stopRef.current) return;

                template.schedules.forEach((s, i) => {
                    newTranslated[i] = {
                        name: translations[`${i}_name`] ?? s.name,
                        ...(s.description ? { description: translations[`${i}_desc`] ?? s.description } : {}),
                        ...(s.push ? {
                            push: {
                                title: translations[`${i}_push_title`] ?? s.push.title,
                                body: translations[`${i}_push_body`] ?? s.push.body,
                                image_url: s.push.image_url,
                            },
                        } : {}),
                    };
                });

                addProcessingLog(`✅ Переведено ${items.length} текстов`);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Ошибка перевода');
                setStep('edit');
                return;
            }
        }

        if (hasInlinePush && appId) {
            addProcessingLog('🔍 Поиск / создание шаблонов...');

            const inlineItems = template.schedules
                .map((s, i) => ({ s, i }))
                .filter(({ s }) => s.push && !s.push_template_id);

            for (let idx = 0; idx < inlineItems.length; idx++) {
                if (stopRef.current) {
                    addProcessingLog('⏹ Остановлено');
                    break;
                }

                const { s, i } = inlineItems[idx];
                const pushContent = newTranslated[i]?.push ?? s.push!;
                const title = pushContent.title;
                const body = pushContent.body;

                const basePct = needTranslate ? 50 : 0;
                const pct = basePct + Math.round(((idx + 1) / inlineItems.length) * (100 - basePct));
                setProgress(pct);
                setProgressLabel(`Шаблон ${idx + 1}/${inlineItems.length}: ${s.name}`);

                const existing = findTemplateByContent(templateCache, title, body);

                if (existing) {
                    newResolvedIds[i] = existing.id;
                    addProcessingLog(`⚡ "${s.name}" → существующий шаблон "${existing.name}" (id:${existing.id})`);
                } else {
                    const templateName = `${newTranslated[i]?.name ?? s.name} [auto]`;
                    try {
                        const result = await dispatch(createTemplate({
                            app_id: appId,
                            name: templateName,
                            description: `Автосоздан при импорте расписания`,
                            title,
                            body,
                            image_url: pushContent.image_url,
                        })).unwrap();

                        const newId = result.template?.id;
                        if (newId) {
                            newResolvedIds[i] = newId;
                            templateCache.push(result.template);
                            addProcessingLog(`✅ "${s.name}" → создан шаблон "${templateName}" (id:${newId})`);
                        }
                    } catch (err: any) {
                        const msg = (err?.message || '').toLowerCase();
                        if (msg.includes('409') || msg.includes('already exist') || msg.includes('duplicate')) {
                            const retry = findTemplateByContent(templateCache, title, body);
                            if (retry) {
                                newResolvedIds[i] = retry.id;
                                addProcessingLog(`⚡ "${s.name}" → конфликт, используем (id:${retry.id})`);
                            } else {
                                addProcessingLog(`❌ "${s.name}" → не удалось создать шаблон: конфликт`);
                            }
                        } else {
                            addProcessingLog(`❌ "${s.name}" → ошибка: ${(err?.message || '').slice(0, 100)}`);
                        }
                    }
                }
            }
        }

        if (stopRef.current) return;

        setTranslatedOverrides(newTranslated);
        setResolvedTemplateIds(newResolvedIds);
        setStep('preview');
    };

    const handleApply = () => {
        if (!template) return;

        const unresolved: number[] = [];
        const result: LoadedSchedule[] = template.schedules.map((item, i) => {
            const merged = getMergedItem(item, i);
            const templateId = merged.push_template_id ?? resolvedTemplateIds[i];

            if (!templateId) {
                unresolved.push(i);
            }

            return {
                name: merged.name,
                description: merged.description,
                schedule_type: merged.schedule_type,
                schedule_value: buildScheduleValue(merged),
                schedule_time: merged.schedule_time,
                timezone: merged.timezone ?? globalTimezone,
                is_active: merged.is_active ?? true,
                push_template_id: templateId ?? 0,
                segment_id: merged.segment_id,
            };
        });

        if (unresolved.length > 0) {
            setError(`Не удалось определить шаблон для расписаний: ${unresolved.map(i => `#${i + 1}`).join(', ')}`);
            return;
        }

        onLoad(result, template.name, targetLang);
        setOpen(false);
    };

    const setOverride = (index: number, patch: Partial<TemplateScheduleItem>) => {
        setOverrides(prev => ({ ...prev, [index]: { ...prev[index], ...patch } }));
    };

    const getSegmentName = (segmentId?: number): string => {
        if (!segmentId) return 'Все пользователи';
        return segments.find(s => s.id === segmentId)?.name ?? `#${segmentId}`;
    };

    const getTemplateBadge = (item: TemplateScheduleItem, index: number) => {
        if (item.push_template_id) {
            return <Tag color="green" style={{ fontSize: 11 }}>ID:{item.push_template_id}</Tag>;
        }
        if (resolvedTemplateIds[index]) {
            return <Tag color="cyan" style={{ fontSize: 11 }}>→ ID:{resolvedTemplateIds[index]}</Tag>;
        }
        if (item.push) {
            return (
                <Tooltip title={`${item.push.title}\n${item.push.body}`}>
                    <Tag color="orange" icon={<ThunderboltOutlined />} style={{ fontSize: 11 }}>
                        Авто
                    </Tag>
                </Tooltip>
            );
        }
        return <Tag color="red" style={{ fontSize: 11 }}>Нет шаблона</Tag>;
    };

    const previewColumns: ColumnsType<TemplateScheduleItem> = [
        {
            title: '№',
            width: 44,
            align: 'center',
            render: (_, __, i) => <Text type="secondary" style={{ fontSize: 12 }}>{i + 1}</Text>,
        },
        {
            title: 'Название',
            render: (_, record, i) => (
                <div>
                    <Space size={6}>
                        <Text strong style={{ fontSize: 13 }}>{record.name}</Text>
                        {getTemplateBadge(record, i)}
                    </Space>
                    {record.description && (
                        <div style={{ fontSize: 12, color: 'token.colorTextTertiary', marginTop: 2 }}>{record.description}</div>
                    )}
                    {record.push && (
                        <div style={{ fontSize: 11, color: 'token.colorTextTertiary', marginTop: 2 }}>
                            📝 {record.push.title} — {record.push.body.slice(0, 60)}{record.push.body.length > 60 ? '…' : ''}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Тип',
            width: 130,
            dataIndex: 'schedule_type',
            render: (type: ScheduleType) => {
                const cfg = SCHEDULE_TYPE_CONFIG[type];
                return <Tag color={cfg.color} icon={<CalendarOutlined />}>{cfg.label}</Tag>;
            },
        },
        {
            title: 'Расписание',
            width: 180,
            render: (_, record) => (
                <Space>
                    <ClockCircleOutlined style={{ color: 'token.colorTextTertiary' }} />
                    <Text style={{ fontSize: 13 }}>{formatScheduleDisplay(record)}</Text>
                </Space>
            ),
        },
        {
            title: 'Сегмент',
            width: 170,
            render: (_, record, i) => {
                const segId = overrides[i]?.segment_id !== undefined
                    ? overrides[i].segment_id
                    : record.segment_id !== undefined ? record.segment_id : globalSegmentId;
                return (
                    <Select
                        size="small" value={segId ?? undefined} placeholder="Все"
                        style={{ width: 150 }} allowClear
                        onChange={(val) => setOverride(i, { segment_id: val ?? undefined })}
                        showSearch optionFilterProp="label"
                        options={segments.map(s => ({ value: s.id, label: s.name }))}
                    />
                );
            },
        },
        {
            title: 'TZ',
            width: 160,
            render: (_, record, i) => {
                const tz = overrides[i]?.timezone ?? record.timezone ?? globalTimezone;
                return (
                    <Select
                        size="small" value={tz} style={{ width: 140 }}
                        onChange={(val) => setOverride(i, { timezone: val })}
                        options={TIMEZONES.map(t => ({ value: t.value, label: t.label }))}
                        showSearch optionFilterProp="label"
                    />
                );
            },
        },
        {
            title: '',
            width: 60,
            align: 'center',
            render: (_, record, i) => {
                const active = overrides[i]?.is_active !== undefined
                    ? overrides[i].is_active
                    : record.is_active !== undefined ? record.is_active : true;
                return (
                    <Badge
                        status={active ? 'success' : 'default'}
                        text={
                            <Text style={{ fontSize: 12, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setOverride(i, { is_active: !active })}
                            >
                                {active ? 'Да' : 'Нет'}
                            </Text>
                        }
                    />
                );
            },
        },
    ];

    const footer = () => {
        if (step === 'upload') return <Button onClick={handleClose}>Отмена</Button>;
        if (step === 'edit') {
            return (
                <Space>
                    <Button onClick={reset}>← Назад</Button>
                    <Button type="primary" onClick={handleProcess}>
                        {(targetLang || inlinePushCount > 0) ? 'Обработать и предпросмотр →' : 'Предпросмотр →'}
                    </Button>
                </Space>
            );
        }
        if (step === 'processing') return <Button danger onClick={handleClose}>Отменить</Button>;
        return (
            <Space>
                <Button onClick={() => { setTranslatedOverrides({}); setResolvedTemplateIds({}); setStep('edit'); }}>
                    ← Изменить
                </Button>
                <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApply}>
                    Применить {template?.schedules.length} расписаний
                </Button>
            </Space>
        );
    };

    const content = () => {
        if (step === 'upload') {
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <FolderOpenOutlined /> Сохранённые пресеты
                        </Text>
                        <div style={{
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 8,
                            maxHeight: 220,
                            overflow: 'auto',
                            background: token.colorFillQuaternary,
                        }}>
                            {presetsLoading && presets.length === 0 ? (
                                <div style={{ padding: 24, textAlign: 'center' }}>
                                    <Spin size="small" />
                                </div>
                            ) : presets.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Нет сохранённых пресетов"
                                    style={{ margin: '16px 0' }}
                                />
                            ) : (
                                <List
                                    size="small"
                                    dataSource={presets}
                                    renderItem={(preset) => {
                                        const count = getPresetScheduleCount(preset.json_data);
                                        return (
                                            <List.Item
                                                style={{ padding: '8px 12px' }}
                                                actions={[
                                                    <Button
                                                        key="load"
                                                        size="small"
                                                        type="primary"
                                                        ghost
                                                        icon={<FolderOpenOutlined />}
                                                        onClick={() => handleLoadPreset(preset)}
                                                    >
                                                        Загрузить
                                                    </Button>,
                                                    <Popconfirm
                                                        key="del"
                                                        title="Удалить пресет?"
                                                        description={`«${preset.name}» — это действие нельзя отменить`}
                                                        okText="Удалить"
                                                        cancelText="Отмена"
                                                        okButtonProps={{ danger: true }}
                                                        onConfirm={() => handleDeletePreset(preset.id)}
                                                    >
                                                        <Button
                                                            size="small"
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                        />
                                                    </Popconfirm>,
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={
                                                        <Space size={6}>
                                                            <Text strong style={{ fontSize: 13 }}>{preset.name}</Text>
                                                            {count !== null && (
                                                                <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                                                                    {count} расп.
                                                                </Tag>
                                                            )}
                                                        </Space>
                                                    }
                                                    description={
                                                        preset.description ? (
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {preset.description}
                                                            </Text>
                                                        ) : null
                                                    }
                                                />
                                            </List.Item>
                                        );
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <Divider style={{ margin: '0' }}>или</Divider>
                    <div>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            JSON с расписаниями. Push-контент определяется тремя способами (приоритет):
                            {' '}<Text code>push_template_id</Text> → готовый шаблон,
                            {' '}<Text code>push</Text> → объект с title/body,
                            {' '}или <Text code>name</Text> + <Text code>description</Text> → автосоздание шаблона из названия и описания.
                        </Text>
                        <pre style={{
                            background: token.colorFillQuaternary,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: 6, padding: '10px 14px',
                            fontSize: 11, color: token.colorTextSecondary,
                            marginBottom: 16, overflow: 'auto', maxHeight: 220,
                        }}>
                            {`{
  "name": "Casino Weekly Pack",
  "lang": "en",
  "default_timezone": "Europe/London",
  "schedules": [
    {
      "name": "Boost Your Week! 🚀",
      "description": "Start your week with a win!",
      "schedule_type": "weekly",
      "schedule_value": "monday",
      "schedule_time": "18:45"
    },
    {
      "name": "Friday Bonus",
      "schedule_type": "weekly",
      "schedule_value": "friday",
      "schedule_time": "12:00",
      "push": { "title": "Weekend Bonus! 🎰", "body": "Get your Friday bonus now" }
    },
    {
      "name": "Monthly Reload",
      "schedule_type": "monthly",
      "schedule_value": "1",
      "schedule_time": "10:00",
      "push_template_id": 42
    }
  ]
}`}
                        </pre>
                        <Upload accept=".json" showUploadList={false} beforeUpload={handleFileRead}>
                            <Button icon={<UploadOutlined />} size="large" block>Выбрать JSON-файл</Button>
                        </Upload>
                    </div>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            <TranslationOutlined /> Язык перевода (необязательно)
                        </Text>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Не переводить (использовать оригинал)"
                            allowClear showSearch optionFilterProp="label"
                            options={LANG_OPTIONS} value={targetLang} onChange={setTargetLang}
                        />
                    </div>
                </Space>
            );
        }

        if (step === 'edit') {
            const isFromPreset = loadedFromPresetId !== undefined;
            return (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <Title level={5} style={{ margin: 0 }}>
                            <FileTextOutlined /> {template?.name ?? 'Набор расписаний'} —{' '}
                            <Text type="secondary">{template?.schedules.length} расписаний</Text>
                        </Title>
                        <Space size={6} wrap>
                            {isFromPreset && (
                                <Tag color="purple" icon={<FolderOpenOutlined />}>Из пресета</Tag>
                            )}
                            {inlinePushCount > 0 && (
                                <Tag color="orange" icon={<ThunderboltOutlined />}>
                                    {inlinePushCount} авто-шаблонов
                                </Tag>
                            )}
                            {targetLang && (
                                <Tag color="blue" icon={<TranslationOutlined />}>
                                    → {SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.abbr ?? targetLang}
                                </Tag>
                            )}
                            {isFromPreset && (
                                <Tooltip title="Обновить существующий пресет">
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={openUpdatePresetDialog}
                                    >
                                        Обновить пресет
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip title="Сохранить как новый пресет">
                                <Button
                                    size="small"
                                    type={isFromPreset ? 'default' : 'primary'}
                                    ghost={!isFromPreset}
                                    icon={<SaveOutlined />}
                                    onClick={openCreatePresetDialog}
                                >
                                    {isFromPreset ? 'Сохранить как новый' : 'Сохранить как пресет'}
                                </Button>
                            </Tooltip>
                        </Space>
                    </div>
                    <div style={{
                        background: token.colorFillQuaternary,
                        borderRadius: 8,
                        padding: '12px 16px',
                        border: `1px solid ${token.colorBorderSecondary}`,
                    }}>
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            <Row align="middle" gutter={12}>
                                <Col><GlobalOutlined style={{ color: '#666' }} /></Col>
                                <Col flex={1}>
                                    <Text strong style={{ fontSize: 13 }}>Timezone по умолчанию</Text>
                                    <div style={{ fontSize: 12, color: 'token.colorTextTertiary' }}>Для расписаний без индивидуального timezone</div>
                                </Col>
                                <Col>
                                    <Select value={globalTimezone} onChange={setGlobalTimezone}
                                        style={{ width: 220 }} showSearch optionFilterProp="label"
                                        options={TIMEZONES.map(t => ({ value: t.value, label: t.label }))}
                                    />
                                </Col>
                            </Row>
                            {segments.length > 0 && (
                                <Row align="middle" gutter={12}>
                                    <Col><TeamOutlined style={{ color: '#666' }} /></Col>
                                    <Col flex={1}>
                                        <Text strong style={{ fontSize: 13 }}>Сегмент по умолчанию</Text>
                                        <div style={{ fontSize: 12, color: 'token.colorTextTertiary' }}>Для расписаний без индивидуального сегмента</div>
                                    </Col>
                                    <Col>
                                        <Select value={globalSegmentId} onChange={setGlobalSegmentId}
                                            style={{ width: 220 }} placeholder="Все пользователи"
                                            allowClear showSearch optionFilterProp="label"
                                            options={segments.map(s => ({ value: s.id, label: s.name }))}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </Space>
                    </div>

                    <Divider style={{ margin: '4px 0' }} />
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        {template?.schedules.map((item, i) => {
                            const merged = getMergedItem(item, i);
                            const cfg = SCHEDULE_TYPE_CONFIG[item.schedule_type];
                            return (
                                <div key={i} style={{
                                    border: `1px solid ${token.colorBorderSecondary}`,
                                    borderRadius: 8,
                                    padding: '10px 14px',
                                    background: token.colorBgElevated,
                                }}>
                                    <Row align="middle" gutter={12}>
                                        <Col flex={1}>
                                            <Space size={8}>
                                                <Tag color={cfg.color} style={{ margin: 0 }}>{cfg.label}</Tag>
                                                <Text strong style={{ fontSize: 13 }}>{item.name}</Text>
                                                {getTemplateBadge(item, i)}
                                            </Space>
                                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                                <ClockCircleOutlined style={{ marginRight: 4, color: 'token.colorTextTertiary' }} />
                                                {formatScheduleDisplay(merged)}
                                                {' · '}
                                                <GlobalOutlined style={{ marginRight: 4, color: 'token.colorTextTertiary' }} />
                                                {merged.timezone ?? globalTimezone}
                                                {' · '}
                                                <TeamOutlined style={{ marginRight: 4, color: 'token.colorTextTertiary' }} />
                                                {getSegmentName(merged.segment_id)}
                                            </div>
                                            {item.push && (
                                                <div style={{ fontSize: 11, color: 'token.colorTextTertiary', marginTop: 3 }}>
                                                    📝 {item.push.title} — {item.push.body.slice(0, 80)}
                                                </div>
                                            )}
                                        </Col>
                                        <Col>
                                            <Badge
                                                status={merged.is_active ? 'success' : 'default'}
                                                text={
                                                    <Text style={{ fontSize: 12, cursor: 'pointer' }}
                                                        onClick={() => setOverride(i, { is_active: !merged.is_active })}>
                                                        {merged.is_active ? 'Активно' : 'Нет'}
                                                    </Text>
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            );
                        })}
                    </Space>
                </Space>
            );
        }

        if (step === 'processing') {
            return (
                <Space direction="vertical" style={{ width: '100%', padding: '16px 0' }} size="middle">
                    <div style={{ textAlign: 'center' }}>
                        <ThunderboltOutlined style={{ fontSize: 32, color: 'token.colorPrimary', marginBottom: 12 }} />
                        <Title level={4} style={{ margin: 0 }}>Обработка...</Title>
                        <Text type="secondary">{progressLabel}</Text>
                    </div>
                    <Progress percent={progress} status="active" />

                    {processingLog.length > 0 && (
                        <div style={{
                            background: '#0d1117', borderRadius: 8, padding: '10px 12px',
                            maxHeight: 200, overflowY: 'auto',
                            fontFamily: 'Consolas, monospace', fontSize: 12, lineHeight: 1.6,
                        }}>
                            {processingLog.map((msg, i) => (
                                <div key={i} style={{
                                    color: msg.startsWith('✅') ? '#56d364'
                                        : msg.startsWith('⚡') ? '#e3b341'
                                            : msg.startsWith('❌') ? '#f85149'
                                                : '#58a6ff',
                                    whiteSpace: 'pre-wrap',
                                }}>
                                    {msg}
                                </div>
                            ))}
                        </div>
                    )}
                </Space>
            );
        }
        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}

                <Alert
                    type="info" showIcon
                    message={`${template?.schedules.length} расписаний готовы к импорту`}
                    description={
                        <Space direction="vertical" size={2}>
                            {targetLang && (
                                <span>Переведено на {SUPPORTED_LANGUAGES.find(l => l.code === targetLang)?.name ?? targetLang}</span>
                            )}
                            {Object.keys(resolvedTemplateIds).length > 0 && (
                                <span>
                                    Шаблоны: {Object.keys(resolvedTemplateIds).length} авто-созданы/найдены
                                </span>
                            )}
                            <span>Проверьте данные перед применением</span>
                        </Space>
                    }
                />

                {processingLog.length > 0 && (
                    <details style={{ fontSize: 12 }}>
                        <summary style={{ cursor: 'pointer', color: 'token.colorTextTertiary', marginBottom: 6 }}>
                            Лог обработки ({processingLog.length} записей)
                        </summary>
                        <div style={{
                            background: '#0d1117', borderRadius: 6, padding: '8px 10px',
                            maxHeight: 140, overflowY: 'auto',
                            fontFamily: 'Consolas, monospace', fontSize: 11, lineHeight: 1.5,
                        }}>
                            {processingLog.map((msg, i) => (
                                <div key={i} style={{
                                    color: msg.startsWith('✅') ? '#56d364'
                                        : msg.startsWith('⚡') ? '#e3b341'
                                            : msg.startsWith('❌') ? '#f85149'
                                                : '#8888aa',
                                }}>
                                    {msg}
                                </div>
                            ))}
                        </div>
                    </details>
                )}

                <Table
                    dataSource={template?.schedules.map((item, i) => getMergedItem(item, i)) ?? []}
                    columns={previewColumns}
                    pagination={false} size="small"
                    rowKey={(_, i) => String(i)}
                    scroll={{ x: 1000 }}
                />
            </Space>
        );
    };

    const stepTitles: Record<Step, string> = {
        upload: 'Импорт расписаний из шаблона',
        edit: 'Настройка параметров',
        processing: 'Обработка',
        preview: 'Предпросмотр расписаний',
    };

    return (
        <>
            <Tooltip title={disabled ? 'Сначала выберите приложение' : 'Загрузить расписания из JSON'}>
                <Button icon={<CalendarOutlined />} disabled={disabled} onClick={handleOpen}>
                    Загрузить расписания из шаблона
                </Button>
            </Tooltip>

            <Modal
                title={stepTitles[step]}
                open={open} onCancel={handleClose}
                footer={footer()}
                width={step === 'preview' ? 1100 : 700}
                destroyOnClose
            >
                {content()}
            </Modal>
            <Modal
                title={savePresetMode === 'update' ? 'Обновить пресет' : 'Сохранить как пресет'}
                open={savePresetOpen}
                onCancel={() => setSavePresetOpen(false)}
                onOk={handleSavePreset}
                confirmLoading={savingPreset}
                okText={savePresetMode === 'update' ? 'Обновить' : 'Сохранить'}
                cancelText="Отмена"
                okButtonProps={{ disabled: !presetName.trim() }}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>
                            Название <Text type="danger">*</Text>
                        </Text>
                        <Input
                            value={presetName}
                            onChange={(e) => setPresetName(e.target.value)}
                            placeholder="Например: Casino Weekly Pack"
                            maxLength={100}
                            showCount
                        />
                    </div>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>
                            Описание
                        </Text>
                        <TextArea
                            value={presetDescription}
                            onChange={(e) => setPresetDescription(e.target.value)}
                            placeholder="Краткое описание содержимого пресета"
                            rows={3}
                            maxLength={500}
                            showCount
                        />
                    </div>
                    <Alert
                        type="info"
                        showIcon
                        message={
                            savePresetMode === 'update'
                                ? `Пресет будет обновлён текущим JSON (${template?.schedules.length} расписаний)`
                                : `Будет сохранён JSON из ${template?.schedules.length} расписаний`
                        }
                    />
                </Space>
            </Modal>
        </>
    );
};

export default RecurringScheduleLoader;